import { jwtDecode } from 'jwt-decode';

import { authClient } from './authClient';

/**
 * Decodes the capability claims from a Better Auth JWT.
 * Synchronous and pure — safe to call during render. Returns an empty array for a
 * missing or malformed token so callers never have to null-check.
 * @param {string|null|undefined} token - The raw JWT access token
 * @returns {string[]} The capabilities claim, or an empty array
 */
export function decodeCapabilities(token) {
	if (!token) return [];
	try {
		return jwtDecode(token)?.capabilities ?? [];
	} catch {
		return [];
	}
}

/**
 * Decodes the platform-level support claims from a Better Auth JWT.
 * Synchronous and pure — safe to call during render. `platform_admin` gates the
 * internal admin UI; `impersonated_by` is only present while an admin is
 * impersonating this session's user.
 * @param {string|null|undefined} token - The raw JWT access token
 * @returns {{ isPlatformAdmin: boolean, impersonatedBy: string|null }}
 */
export function decodePlatformClaims(token) {
	if (!token) return { isPlatformAdmin: false, impersonatedBy: null };
	try {
		const claims = jwtDecode(token);
		return {
			isPlatformAdmin: claims?.platform_admin === true,
			impersonatedBy: claims?.impersonated_by ?? null,
		};
	} catch {
		return { isPlatformAdmin: false, impersonatedBy: null };
	}
}

//JWT Cache
let cachedAccessToken = null;
let cachedCapabilities = null;

/**
 * Drops the module-level JWT cache. MUST be called on sign-out and before sign-in:
 * the token stays valid for up to 1h server-side, so a stale cache would let the
 * next user on this tab keep acting as the previous identity.
 * @returns {void}
 */
export function clearCachedAuthToken() {
	cachedAccessToken = null;
	cachedCapabilities = null;
}

/**
 * The current user's id from the Better Auth session, or null when unauthenticated.
 * @returns {Promise<string|null>}
 */
export async function getUserId() {
	const { data } = await authClient.getSession();
	return data?.user?.id ?? null;
}

function isTokenExpired(token) {
	try {
		const { exp } = jwtDecode(token);
		// expired if less than 30 seconds remaining
		return !exp || Date.now() >= (exp - 30) * 1000;
	} catch {
		return true;
	}
}

/**
 * Fetch a fresh Better Auth JWT (the jwt plugin's /token endpoint returns a signed JWT the
 * backend's authMiddleware verifies against the JWKS). Returns null when unauthenticated.
 * @returns {Promise<string|null>}
 */
async function getBetterAuthToken() {
	try {
		const { data } = await authClient.$fetch('/token', { method: 'GET' });
		return data?.token ?? null;
	} catch {
		return null;
	}
}

/**
 * The Bearer JWT for backend requests (cached until it is close to expiry).
 * @returns {Promise<string|null>}
 */
export async function getAccessToken() {
	if (cachedAccessToken && !isTokenExpired(cachedAccessToken)) {
		return cachedAccessToken;
	}

	cachedAccessToken = await getBetterAuthToken();
	cachedCapabilities = decodeCapabilities(cachedAccessToken);

	return cachedAccessToken;
}

/* Get all user capabilities */
export async function getUserCapabilities() {
	if (cachedCapabilities === null) {
		const token = await getAccessToken();
		cachedCapabilities = decodeCapabilities(token);
	}
	return cachedCapabilities;
}

/* Check if user has at least one of the required capabilities */
export async function hasCapability(required) {
	const caps = await getUserCapabilities();
	return caps.includes(required);
}

//Force a refresh of the capabilities (needed e.g. after changing user roles).
//Better Auth mints a fresh JWT per /token call with current claims — just drop the cache.
export async function forceSyncCapabilities() {
	cachedAccessToken = null;
	cachedAccessToken = await getBetterAuthToken();
	cachedCapabilities = decodeCapabilities(cachedAccessToken);
	return cachedCapabilities;
}
